import React from 'react';
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx/xlsx.mjs';
import { saveXLSXWithDialog } from "../../Utils/FileSaveDialog";

interface RootState {
    pickAFile: {
        actions: any[];
        layout: any[];
        questions: any[];
        rules: any[];
        serviceBrandQuestions: any[];
        serviceQuestions: any[];
        fileName: string;
        fileLength: number | null;
        businessRules: any[];
    };
    refreshLoadedFile: { isLoading: boolean };
}

const SaveAsCSVFile: React.FC = () => {
    const currentActions = useSelector((state: RootState) => state.pickAFile.actions);
    const currentLayout = useSelector((state: RootState) => state.pickAFile.layout);
    const currentQuestions = useSelector((state: RootState) => state.pickAFile.questions);
    const currentRules = useSelector((state: RootState) => state.pickAFile.rules);
    const currentBrands = useSelector((state: RootState) => state.pickAFile.serviceBrandQuestions);
    const currentServices = useSelector((state: RootState) => state.pickAFile.serviceQuestions);
    const currentFileName = useSelector((state: RootState) => state.pickAFile.fileName);
    const currentFileLength = useSelector((state: RootState) => state.pickAFile.fileLength);
    const isLoading = useSelector((state: RootState) => state.refreshLoadedFile.isLoading);
    const currentBusinessRules = useSelector((state: RootState) => state.pickAFile.businessRules);

    const allTabs = [
        { key: 'patient', text: 'Patient', icon: 'user', index: 0 },
        { key: 'insurance', text: 'Insurance', icon: 'dollar sign', index: 1 },
        { key: 'income & eligibility', text: 'Income & Eligibility', icon: 'money bill alternate', index: 2 },
        { key: 'medical', text: 'Medical', icon: 'heartbeat', index: 3 },
        { key: 'provider', text: 'Provider', icon: 'stethoscope', index: 4 },
        { key: 'order / logistics', text: 'Order & Logistics', icon: 'clipboard list', index: 5 },
        { key: 'other', text: 'Other', icon: 'info', index: 6 },
        { key: 'attestation & signature', text: 'Attestation & Signature', icon: 'clipboard check', index: 7 }
    ]

    let tabs = getGroupedTabs();
    tabs?.sort((a, b) => sortLayoutGroups(a, b));

    const generateExcel = async () => {
        let wb = XLSX.utils.book_new();

        generateFormOptionGroupsTab(wb);
        generateMarkdownTab(wb);
        generateQuestionsTab(wb);
        generateRulesTab(wb);
        generateBusinessRulesTab(wb);

        let csvFileName = currentFileName.toLowerCase().replace(".json", ".xlsx").substring(currentFileName.lastIndexOf("\\") + 1);

        // Use native save dialog instead of automatic download
        await saveXLSXWithDialog(XLSX, wb, csvFileName);
    }

    function getGroupedTabs() {
        if (currentLayout) {
            let groupedByTabs = currentLayout?.reduce((layout, {
                tab,
                fields,
                name
            }) => {
                (layout[tab] = layout[tab] || []).push({ tab, fields, name });
                return layout;
            }, {});

            return Object.entries(groupedByTabs);
        }
        return null;
    }

    function sortLayoutGroups(a, b) {
        let tabA = a[0];
        let tabB = b[0];

        let foundA = allTabs.filter(tab => tab.key === tabA);
        let foundB = allTabs.filter(tab => tab.key === tabB);

        let indexA = foundA[0].index;
        let indexB = foundB[0].index;

        if (indexA < indexB) {
            return -1;
        }
        if (indexA > indexB) {
            return 1;
        }
    }

    function generateFormOptionGroupsTab(wb) {
        //Only include formOptionGroups with a unique listName.
        let formOptionGroups = [...new Map(currentQuestions.filter(q => q.listName != null).map(item => [item["listName"], item])).values()];

        let formOptionGroupsWs = XLSX.utils.aoa_to_sheet([
            ["Form Option Group Identifier", "Form Option Item Screen Name", "Form Option Item Database Value", "IsDefault"]
        ]);

        formOptionGroups.forEach(option => {
            let formOptionIndex = 0;

            option.values.forEach(value => {
                if (formOptionIndex == 0) {
                    XLSX.utils.sheet_add_aoa(formOptionGroupsWs, [[
                        option.listName,
                        option.options[formOptionIndex],
                        value,
                        ""
                    ]], { origin: - 1 });
                }
                else {
                    XLSX.utils.sheet_add_aoa(formOptionGroupsWs, [[
                        ,
                        option.options[formOptionIndex],
                        value,
                        ""
                    ]], { origin: - 1 });
                }

                formOptionIndex++;
            })
        })

        XLSX.utils.book_append_sheet(wb, formOptionGroupsWs, "FormOptionGroups", true);
    }

    function generateMarkdownTab(wb) {
        let markDowns = currentQuestions.filter(q => q.markdown != null)

        let markDownsWs = XLSX.utils.aoa_to_sheet([
            ["Markdown Identifier", "Markdown"]
        ]);

        markDowns.forEach(mark => {
            XLSX.utils.sheet_add_aoa(markDownsWs, [[
                mark.dataKey,
                mark.markdown
            ]], { origin: - 1 });
        })

        XLSX.utils.book_append_sheet(wb, markDownsWs, "Markdown", true);
    }

    function generateQuestionsTab(wb) {
        let requiredTabs = [
            "patient",
            "insurance",
            "income & eligibility",
            "medical",
            "provider",
            "order / logistics",
            "other",
            "attestation & signature"
        ];
        let currentTabs = tabs.map(function (x) {
            return x[0];
        });

        //If the current tabs do not contain any of the required ones they need to be added.
        requiredTabs.map(function (x) {
            if (!currentTabs.includes(x)) {
                tabs.push([x, []])
            }
        })

        tabs.forEach((tab: any) => {
            let index = 2;

            let questionsWs = XLSX.utils.aoa_to_sheet([
                ["Form Section", "Field Name Label", "Field DataName", "Required", "IsSensitive", "MaxLength", "MinLength", "Data Type", "Form Option Group Identifier", "ServiceIDs", "BrandIDs"]
            ]);

            tab[1].map((currentCategory: any) => {
                let address = "A" + index;

                currentCategory.fields.map((currentField: any) => {
                    var question = currentQuestions.filter(q => q.dataKey == currentField);
                    var currentServiceIds = currentServices.filter(c => c.questionId == question[0].questionId).map(c => { return String(c.assistanceServiceId) });
                    //Prevent duplicates in array using Array.from(new Set()).
                    var currentBrandIds = Array.from(new Set(currentBrands.filter(c => c.questionId == question[0].questionId).map(c => { return String(c.brandId) })));

                    XLSX.utils.sheet_add_aoa(questionsWs, [[, question[0]?.label,
                        question[0]?.dataKey,
                        question[0]?.required,
                        question[0]?.sensitive ? 1 : 0,
                        question[0]?.maxLength,
                        question[0]?.minLength,
                        question[0]?.selectedType,
                        question[0]?.listName,
                        currentServiceIds?.join("|"),
                        currentBrandIds?.join("|")
                    ]], { origin: - 1 });

                    index++;
                })

                XLSX.utils.sheet_add_aoa(questionsWs, [[currentCategory.name]], { origin: address });
            })

            let words = tab[0].split(" ")
            let correctTabCase = words.map((word) => {
                return word[0].toUpperCase() + word.substring(1);
            }).join(" ");

            //Sheet name cannot contain ": \ / ? * [ ]".
            correctTabCase = correctTabCase.replace(":", "");
            correctTabCase = correctTabCase.replace("\\", "");
            correctTabCase = correctTabCase.replace("/", "");
            correctTabCase = correctTabCase.replace("?", "");
            correctTabCase = correctTabCase.replace("*", "");
            correctTabCase = correctTabCase.replace("[", "");
            correctTabCase = correctTabCase.replace("]", "");

            //Need to do this in a hacky way because we cannot have
            //a "/" in the tab name so the Order / Logistic tab name
            //appears as below but we need the & in the name.
            if (correctTabCase == "Order  Logistics") {
                correctTabCase = "Order & Logistics";
            }

            XLSX.utils.book_append_sheet(wb, questionsWs, correctTabCase, true);
        })
    }

    function generateRulesTab(wb) {
        let rulesWs = XLSX.utils.aoa_to_sheet([
            ["RuleGroup Identifier", "Field DataName", "Operator", "ValueMatch", "Conjuction", "Rule Action DataName", "IsRequired", "IsHidden", "IsReadonly"]
        ]);

        let fieldDataName = "";
        let operator = "";
        let conjuction = "";
        let valueMatch = "";
        let conjunctionCount = 0;
        let ruleGroupIdentifierIndex = 1;

        currentRules.forEach(rule => {
            let uniqueParams = [];

            //Get initial condition values.
            if (rule.conditions.all) {
                operator = rule.conditions.all[0].operator === "equal" ? "EqualTo"
                    : rule.conditions.all[0].operator === "notEqual" ? "NotEqualTo"
                        : rule.conditions.all[0].operator === "contains" ? "Contains"
                            : rule.conditions.all[0].operator

                fieldDataName = rule.conditions.all[0].fact;
                valueMatch = rule.conditions.all[0].value;
                conjuction = rule.name?.includes("AND") ? "And" : ""

                conjunctionCount = (rule.name?.match(/AND/g) || []).length;
            }
            else if (rule.conditions.any) {
                operator = rule.conditions.any[0].operator === "equal" ? "EqualTo"
                    : rule.conditions.any[0].operator === "notEqual" ? "NotEqualTo"
                        : rule.conditions.any[0].operator === "contains" ? "Contains"
                            : rule.conditions.any[0].operator

                fieldDataName = rule.conditions.any[0].fact;
                valueMatch = rule.conditions.any[0].value;
                conjuction = rule.name?.includes("OR") ? "Or" : ""

                conjunctionCount = (rule.name?.match(/OR/g) || []).length;
            }

            rule.actions = currentActions.filter(a => a.ruleId == rule.event.type)

            rule.actions.forEach(param => {
                param.params.forEach(p => {
                    uniqueParams.push(p)
                })
            })

            //Creating a single object combining both currentRules and currentActions.
            let uniqueChars = Array.from(new Set(uniqueParams));
            let index = 0;

            uniqueChars.forEach(char => {
                let isRequired = 0;
                let isHidden = 0;
                let isReadonly = 0;
                rule.actions.forEach(act => {
                    if (act.func == "require" && act.params.includes(char)) {
                        isRequired = 1;
                    }
                    if (act.func == "hide" && act.params.includes(char)) {
                        isHidden = 1;
                    }
                    if (act.func == "readonly" && act.params.includes(char)) {
                        isReadonly = 1;
                    }
                })

                if (conjunctionCount != 0) {
                    //If there is a conjunction but only one rule action data name.
                    if (conjunctionCount > index && uniqueChars.length == 1) {
                        let conjunctionIndex = 0;

                        if (rule.conditions.all) {
                            rule.conditions.all.forEach(all => {
                                operator = all.operator === "equal" ? "EqualTo"
                                    : all.operator === "notEqual" ? "NotEqualTo"
                                        : all.operator === "contains" ? "Contains"
                                            : all.operator

                                fieldDataName = all.fact;
                                valueMatch = all.value;
                                conjuction = rule.name?.includes("AND") ? "And" : ""

                                if (conjunctionIndex == 0) {
                                    XLSX.utils.sheet_add_aoa(rulesWs, [[
                                        "RuleGroup_Identifier_" + ruleGroupIdentifierIndex, //We do not capture this in the data model tool currently.
                                        fieldDataName,
                                        operator,
                                        valueMatch,
                                        ,
                                        char,
                                        isRequired,
                                        isHidden,
                                        isReadonly
                                    ]], { origin: - 1 });

                                    conjunctionIndex++;
                                }
                                else {
                                    XLSX.utils.sheet_add_aoa(rulesWs, [[
                                        "RuleGroup_Identifier_" + ruleGroupIdentifierIndex, //We do not capture this in the data model tool currently.
                                        fieldDataName,
                                        operator,
                                        valueMatch,
                                        conjuction
                                        ,
                                        ,
                                        ,

                                    ]], { origin: - 1 });
                                }
                            })
                        }
                        else if (rule.conditions.any) {
                            rule.conditions.any.forEach(any => {
                                operator = any.operator === "equal" ? "EqualTo"
                                    : any.operator === "notEqual" ? "NotEqualTo"
                                        : any.operator === "contains" ? "Contains"
                                            : any.operator

                                fieldDataName = any.fact;
                                valueMatch = any.value;
                                conjuction = rule.name?.includes("OR") ? "Or" : ""

                                if (conjunctionIndex == 0) {
                                    XLSX.utils.sheet_add_aoa(rulesWs, [[
                                        "RuleGroup_Identifier_" + ruleGroupIdentifierIndex, //We do not capture this in the data model tool currently.
                                        fieldDataName,
                                        operator,
                                        valueMatch,
                                        ,
                                        char,
                                        isRequired,
                                        isHidden,
                                        isReadonly
                                    ]], { origin: - 1 });

                                    conjunctionIndex++;
                                }
                                else {
                                    XLSX.utils.sheet_add_aoa(rulesWs, [[
                                        "RuleGroup_Identifier_" + ruleGroupIdentifierIndex, //We do not capture this in the data model tool currently.
                                        fieldDataName,
                                        operator,
                                        valueMatch,
                                        conjuction
                                        ,
                                        ,
                                        ,

                                    ]], { origin: - 1 });
                                }
                            })
                        }
                    }
                    //If there is a normal conjunction.
                    else {
                        if (rule.conditions.all?.length > index) {
                            operator = rule.conditions.all[index].operator === "equal" ? "EqualTo"
                                : rule.conditions.all[index].operator === "notEqual" ? "NotEqualTo"
                                    : rule.conditions.all[index].operator === "contains" ? "Contains"
                                        : rule.conditions.all[index].operator

                            fieldDataName = rule.conditions.all[index].fact;
                            valueMatch = rule.conditions.all[index].value;
                            conjuction = rule.name?.includes("AND") ? "And" : ""
                        }
                        else if (rule.conditions.any?.length > index) {
                            operator = rule.conditions.any[index].operator === "equal" ? "EqualTo"
                                : rule.conditions.any[index].operator === "notEqual" ? "NotEqualTo"
                                    : rule.conditions.any[index].operator === "contains" ? "Contains"
                                        : rule.conditions.any[index].operator

                            fieldDataName = rule.conditions.any[index].fact;
                            valueMatch = rule.conditions.any[index].value;
                            conjuction = rule.name?.includes("OR") ? "Or" : ""
                        }

                        if (index == 0) {
                            XLSX.utils.sheet_add_aoa(rulesWs, [[
                                "RuleGroup_Identifier_" + ruleGroupIdentifierIndex, //We do not capture this in the data model tool currently.
                                fieldDataName,
                                operator,
                                valueMatch,
                                ,
                                char,
                                isRequired,
                                isHidden,
                                isReadonly
                            ]], { origin: - 1 });
                        }
                        else if (index > conjunctionCount) {
                            XLSX.utils.sheet_add_aoa(rulesWs, [[
                                "RuleGroup_Identifier_" + ruleGroupIdentifierIndex, //We do not capture this in the data model tool currently.
                                ,
                                ,
                                ,
                                ,
                                char,
                                isRequired,
                                isHidden,
                                isReadonly
                            ]], { origin: - 1 });
                        }
                        else {
                            XLSX.utils.sheet_add_aoa(rulesWs, [[
                                "RuleGroup_Identifier_" + ruleGroupIdentifierIndex, //We do not capture this in the data model tool currently.
                                fieldDataName,
                                operator,
                                valueMatch,
                                conjuction,
                                char,
                                isRequired,
                                isHidden,
                                isReadonly
                            ]], { origin: - 1 });
                        }
                    }
                }

                if (conjunctionCount == 0) {
                    //If there is no conjunction.
                    if (index == 0) {
                        XLSX.utils.sheet_add_aoa(rulesWs, [[
                            "RuleGroup_Identifier_" + ruleGroupIdentifierIndex, //We do not capture this in the data model tool currently.
                            fieldDataName,
                            operator,
                            valueMatch,
                            ,
                            char,
                            isRequired,
                            isHidden,
                            isReadonly
                        ]], { origin: - 1 });
                    }
                    else {
                        XLSX.utils.sheet_add_aoa(rulesWs, [[
                            "RuleGroup_Identifier_" + ruleGroupIdentifierIndex, //We do not capture this in the data model tool currently.
                            ,
                            ,
                            ,
                            ,
                            char,
                            isRequired,
                            isHidden,
                            isReadonly
                        ]], { origin: - 1 });
                    }
                }

                index++;
            })

            //Spacing rules out after each iteration.
            XLSX.utils.sheet_add_aoa(rulesWs, [[
                ,
                ,
                ,
                ,
                ,
                ,

            ]], { origin: - 1 });

            ruleGroupIdentifierIndex++;
        })
        XLSX.utils.book_append_sheet(wb, rulesWs, "Rules", true);
    }

    function generateBusinessRulesTab(wb) {
        let buesinessRulesWs = XLSX.utils.aoa_to_sheet([
            ["Business Rules"]
        ]);

        XLSX.utils.sheet_add_aoa(buesinessRulesWs, [[currentBusinessRules]], { origin: - 1 });

        XLSX.utils.book_append_sheet(wb, buesinessRulesWs, "Business Rules", true);
    }

    const csvFile = (
        <button disabled={currentFileLength == null || isLoading} onClick={generateExcel} title="Download CSV">Download CSV</button>
    )

    return csvFile;
}

export default SaveAsCSVFile;